/**
 * Sino de notificações no topo: o que aconteceu na conta (saúde da conta nos canais: métrica fora da meta, ponto de
 * penalidade, restrição, anúncio com problema, pedido atrasado, aviso do canal). Não lidas primeiro, com o número no
 * sino; abrir uma leva à tela certa e marca como lida (para este usuário). Atualiza a cada 2 minutos.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';
import { api, dateTime } from '../api.ts';

interface Notif { id: number; kind: string; level: string; title: string; body: string | null; link: string | null; createdAt: string; read: boolean; account: string | null; channel: string | null }
const DOT: Record<string, string> = { critical: 'var(--err)', attention: 'var(--warn)', info: 'var(--info, #3b82f6)' };
const CH: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood' };

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api<{ unread: number; items: Notif[] }>('/notifications?limit=30'), refetchInterval: 120_000 });
  const read = useMutation({
    mutationFn: (b: { ids?: number[]; all?: boolean }) => api('/notifications/read', { method: 'POST', json: b }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = q.data?.unread ?? 0;
  const open = (n: Notif) => {
    if (!n.read) read.mutate({ ids: [n.id] });
    if (!n.link) return;
    if (/^https?:\/\//.test(n.link)) window.open(n.link, '_blank', 'noopener');
    else navigate(n.link);
  };
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="icon-btn" aria-label={unread ? `Notificações: ${unread} não lida(s)` : 'Notificações'} style={{ position: 'relative', color: 'var(--band-ink)' }}>
          <Bell size={19} />
          {unread > 0 && <span style={{ position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: 'var(--err)',
            color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>{unread > 99 ? '99+' : unread}</span>}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content className="menu" align="end" sideOffset={8} style={{ width: 380, maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px' }}>
            <b style={{ flex: 1 }}>Notificações</b>
            {unread > 0 && <button type="button" className="btn sm quiet" onClick={() => read.mutate({ all: true })}>Marcar todas como lidas</button>}
          </div>
          <div className="menu-sep" />
          {!q.data ? <div className="small muted" style={{ padding: 10 }}>Carregando…</div>
            : !q.data.items.length ? <div className="small muted" style={{ padding: 10 }}>Nenhuma notificação.</div>
            : q.data.items.map((n) => (
              <Dropdown.Item key={n.id} className="menu-item" onSelect={() => open(n)} style={{ alignItems: 'flex-start', gap: 8, whiteSpace: 'normal', opacity: n.read ? 0.65 : 1 }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, flex: 'none', background: n.read ? 'transparent' : DOT[n.level] ?? 'var(--muted)',
                  border: `1.5px solid ${DOT[n.level] ?? 'var(--muted)'}` }} />
                <span style={{ display: 'grid', gap: 2 }}>
                  <span style={{ fontWeight: n.read ? 500 : 600 }}>{n.title}</span>
                  {n.body && <span className="small muted">{n.body.length > 160 ? `${n.body.slice(0, 160)}…` : n.body}</span>}
                  <span className="small muted">{dateTime(n.createdAt)}{n.channel ? ` · ${CH[n.channel] ?? n.channel}${n.account ? ` · ${n.account}` : ''}` : ''}</span>
                </span>
              </Dropdown.Item>
            ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
